import {
  Target, Edit2, Save, X, Check,
  BarChart2, Award, Phone, FileCheck, DollarSign, TrendingUp,
} from "lucide-react";

import LockedPlaceholder  from "../shared/LockedPlaceholder";
import ProgressBar        from "../shared/ProgressBar";
import NinetyDayRoadmap   from "../goals/NinetyDayRoadmap";
import AISetupSection     from "../goals/AISetupSection";
import CustomGoals        from "../goals/CustomGoals";
import { useGoals }       from "../../hooks/useGoals";
import { GOALS_CONFIG, ACHIEVEMENTS } from "../../data/goalsData";
import { resolveIcon, formatMetricValue, COLOR_CLASS_MAP } from "../../utils/profileHelpers";
import { formatCurrency } from "../../lib/format";

/**
 * GoalsTab
 * Monthly targets (JSON-driven), custom goals, YTD summary,
 * achievements, 90-day roadmap, and AI setup placeholder.
 *
 * Props:
 *   locked – when true shows LockedPlaceholder instead of content
 */
export default function GoalsTab({ locked }) {
  const {
    targets, draft, setDraft, editing, setEditing, saved, handleSave,
    extraGoals, addGoal, removeGoal, goalsData: g,
  } = useGoals();

  if (locked) {
    return <LockedPlaceholder title="Goals Locked" desc="Complete your onboarding to set and track your performance goals." />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Monthly targets — driven by GOALS_CONFIG.metrics */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <Target size={15} style={{ display: "inline", marginRight: 6 }} />Monthly Targets
          </span>
          {!editing ? (
            <button className="btn btn-secondary btn-sm" onClick={() => { setDraft({ ...targets }); setEditing(true); }}>
              <Edit2 size={13} /> Edit Goals
            </button>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleSave}><Save size={13} /> Save</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}><X size={13} /> Cancel</button>
            </div>
          )}
        </div>

        <div className="card-body">
          {saved && <p className="settings-success" style={{ marginBottom: 12 }}><Check size={14} />Goals updated</p>}

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {GOALS_CONFIG.metrics.map(m => {
              const current = g[m.currentKey] ?? 0;
              const target  = targets[m.targetKey] ?? 1;
              const pct     = target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;
              const color   = COLOR_CLASS_MAP[m.colorClass] ?? "#2563eb";
              const iconEl  = resolveIcon(m.iconName, 18, color);
              const fmt     = v => formatMetricValue(v, m.format);

              return (
                <div key={m.targetKey}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className={`stat-icon ${m.colorClass}`} style={{ width: 34, height: 34, borderRadius: 8 }}>
                        {iconEl}
                      </div>
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>{m.label}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          {fmt(current)} of{" "}
                          {editing ? (
                            <input
                              type="number"
                              value={draft[m.targetKey]}
                              onChange={e => setDraft(d => ({ ...d, [m.targetKey]: Number(e.target.value) }))}
                              style={{ width: 72, padding: "1px 6px", border: "1px solid #2563eb", borderRadius: 4, fontSize: 12, color: "#0f172a" }}
                            />
                          ) : fmt(target)}{" "}target
                        </div>
                      </div>
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: pct >= 100 ? "#16a34a" : "#0f172a" }}>
                      {pct}%
                    </span>
                  </div>
                  <ProgressBar current={current} target={target} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dynamic custom goals */}
      <CustomGoals extraGoals={extraGoals} addGoal={addGoal} removeGoal={removeGoal} />

      {/* YTD summary */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <BarChart2 size={15} style={{ display: "inline", marginRight: 6 }} />Year-to-Date Summary
          </span>
        </div>
        <div className="card-body">
          <div className="stats-grid">
            <div className="stat-card"><div className="stat-icon blue"><Phone size={20} color="#2563eb" /></div><div><div className="stat-label">Total Calls</div><div className="stat-value">{g.ytd_calls}</div></div></div>
            <div className="stat-card"><div className="stat-icon purple"><FileCheck size={20} color="#7c3aed" /></div><div><div className="stat-label">Loans Closed</div><div className="stat-value">{g.ytd_loans}</div></div></div>
            <div className="stat-card"><div className="stat-icon green"><DollarSign size={20} color="#16a34a" /></div><div><div className="stat-label">Revenue</div><div className="stat-value">{formatCurrency(g.ytd_revenue, { compact: true })}</div></div></div>
            <div className="stat-card"><div className="stat-icon orange"><TrendingUp size={20} color="#ea580c" /></div><div><div className="stat-label">Conversion Rate</div><div className="stat-value">{g.ytd_loans > 0 ? Math.round((g.ytd_loans / g.ytd_calls) * 100) : 0}%</div></div></div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <Award size={15} style={{ display: "inline", marginRight: 6 }} />Achievements
          </span>
        </div>
        <div className="card-body">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            {ACHIEVEMENTS.map(a => (
              <div key={a.label} style={{
                border: "1px solid", borderColor: a.earned ? "#bfdbfe" : "#e2e8f0",
                borderRadius: 10, padding: "14px 12px", textAlign: "center",
                background: a.earned ? "#eff6ff" : "#f8fafc", opacity: a.earned ? 1 : 0.55,
              }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{a.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: a.earned ? "#1e40af" : "#64748b", marginBottom: 4 }}>{a.label}</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 90-Day Roadmap */}
      <NinetyDayRoadmap />

      {/* AI Setup placeholder */}
      <AISetupSection />

    </div>
  );
}